<script>
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import AlertBasic from '@components/notifications/alert-basic.vue';
import ProspectCommunicationHistory from '@components/prospects/prospect-communication-history.vue';
import ProspectContactForm from '@components/forms/prospect-contact-form.vue';
import ProspectForm from '@components/forms/prospect-form.vue';
import ProspectUnsubscribedNotice from '@components/prospects/prospect-unsubscribed-notice.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  alertComputed,
  alertMethods,
  authComputed,
  campaignComputed,
  campaignMethods,
  prospectComputed,
  prospectMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Prospect Profile',
    meta: [{ name: 'description', content: 'The Prospect Profile page.' }],
  },
  components: {
    AdminPanel,
    AlertBasic,
    ProspectCommunicationHistory,
    ProspectContactForm,
    ProspectForm,
    ProspectUnsubscribedNotice,
  },
  props: {},
  mixins: [adminPanelMixin],
  data() {
    return {
      alertTimeoutId: null,
      showMsgOpts: false,
    };
  },
  computed: {
    ...alertComputed,
    ...authComputed,
    ...adminPanelComputed,
    ...campaignComputed,
    ...prospectComputed,
    prospectFullName() {
      return this.selectedProspect?.firstName && this.selectedProspect?.lastName
        ? `${this.selectedProspect.firstName} ${this.selectedProspect.lastName}`
        : `${this.selectedProspect.firstName}`;
    },
    hasUnsubscribed() {
      return this.selectedProspect?.unsubscribed;
    },
    hasValidPhoneConnOpts() {
      const phoneConnOpts = this.selectedProspect?.connectionOptions?.find(
        (contact) => contact.type === 'phone'
      );
      return phoneConnOpts?.value;
    },
    hasValidEmailConnOpts() {
      const emailConnOpts = this.selectedProspect?.connectionOptions?.find(
        (contact) => contact.type === 'email'
      );
      return emailConnOpts?.value;
    },
  },
  async mounted() {
    if (this.selectedProspect) {
      await this.splitProspectConnOpts();
    }
  },
  destroyed() {
    this.resetTimeout();
  },
  methods: {
    ...adminPanelMethods,
    ...alertMethods,
    ...campaignMethods,
    ...prospectMethods,
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    async composeMsg(type) {
      if (!this.hasUnsavedProspectChanges) {
        await this.setSelectedCampaign(this.campaigns[0]);
        const msgType = type === 'Phone' ? 'SMS' : type;
        this.showMsgOpts = false;
        const data = {
          panelTitle: `Compose ${msgType} to ${this.prospectFullName}`,
          msgType: type.toLowerCase(),
          showResetButton: true,
        };
        this.showAdminPanel('ProspectContactForm', data);
      } else {
        this.$bvModal.show('unsaved-changes-modal');
      }
    },
    onShowMsgOpts() {
      if (!this.selectedProspect?.unsubscribed) {
        this.showMsgOpts = !this.showMsgOpts;
      }
    },
    onResetAdminPanel() {
      this.setOverriddenCampaignTemplate(null);
    },
    onHidePanel() {
      if (this.overriddenCampaignTemplate) {
        this.$bvModal.show('unsaved-changes-modal');
      }
    },
    resetTimeout() {
      if (this.alertTimeoutId) {
        clearTimeout(this.alertTimeoutId);
      }
    },
  },
  watch: {
    showAlert(newVal, oldVal) {
      if (newVal) {
        this.alertTimeoutId = setTimeout(() => {
          this.setShowAlert();
        }, 3000);
      } else {
        this.resetTimeout();
      }
    },
  },
};
</script>

<template>
  <div>
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      @reset-admin-panel="onResetAdminPanel"
      @hide-panel="onHidePanel"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
      />
    </AdminPanel>

    <div class="loading-wrapper" v-if="!selectedProspect">
      <BaseLoadingSpinner />
    </div>
    <div v-else>
      <AlertBasic :hideCloseBtn="true">
        <span class="alert-msg" v-if="alertText">
          {{ alertText }}
        </span>
      </AlertBasic>
      <div class="row">
        <div class="col-7">
          <ProspectUnsubscribedNotice
            v-if="hasUnsubscribed"
            :prospect="selectedProspect"
          />
        </div>
        <div class="col-5 send-btn-wrapper">
          <BaseButton
            type="button"
            class="btn-primary btn-round"
            :disabled="hasUnsubscribed"
            @click="onShowMsgOpts"
          >
            Send Campaign <span class="icon-divider"></span>
            <BaseIcon class="icon-caret-down" name="caret-down" />
          </BaseButton>
          <div class="message-options" v-if="showMsgOpts">
            <ul>
              <li
                :class="{ disabled: !hasValidPhoneConnOpts }"
                @click.prevent="composeMsg('Phone')"
                >SMS</li
              >
              <li
                :class="{ disabled: !hasValidEmailConnOpts }"
                @click.prevent="composeMsg('Email')"
                >Email</li
              >
            </ul>
          </div>
        </div>
      </div>

      <ProspectForm
        v-if="selectedProspect.phoneConnOpts || selectedProspect.phoneConnOpts"
        :prospect="selectedProspect"
      />
      <ProspectCommunicationHistory :prospect="selectedProspect" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.alert {
  top: 11.3rem;
}

.send-btn-wrapper {
  align-items: center;
  display: flex;
  flex-direction: row-reverse;

  .icon-divider {
    border-left: 1px solid white;
    margin: 0 0.9rem 0 0.7rem;
    padding-left: 0.3rem;
  }

  button:disabled,
  li.disabled {
    opacity: 0.5;
  }
  li.disabled {
    pointer-events: none;
  }
  .message-options {
    background: white;
    border-radius: $base-border-radius-md;
    box-shadow: 0 0 0.4rem rgba($color-black-wo-rgb, 0.15);
    padding: 1.8rem 1.8rem 0;
    position: absolute;
    right: unset;
    top: 4.7rem;
    width: 17.4rem;
    z-index: 1;

    &:hover {
      cursor: default;
    }

    li {
      color: black;
      flex-grow: 0;
      font-size: 1.4rem;
      font-weight: 300;
      letter-spacing: -0.257143px;
      line-height: 2.4rem;
      margin: 0.6rem 0;
      order: 1;

      &:hover {
        cursor: pointer;
      }
    }
  }
}

::v-deep .action-footer-wrapper {
  padding-right: 4rem;
}

.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 30vh 0 35vh;
  width: 100%;

  ::v-deep .loading {
    height: 4rem;
    width: 4rem;
  }
}
</style>
